import { useState, useEffect, useRef } from 'react';
import { supabase, type Usuario } from '../../lib/supabase';
import { useSucursal } from '../../utils/sucursalContext';
import AdminLayout from '../../components/layout/AdminLayout';

type Plan = {
  id: string;
  nombre: string;
  descripcion: string | null;
  precio: number;
  duracion_dias: number;
  max_dias_semana?: number | null;
  activo?: boolean | null;
  color?: string | null;
};

type BiometriaFacialEstado = 'enrolado' | 'pendiente_validacion' | 'rechazado';

type BiometriaFacialRow = {
  usuario_id: string;
  estado: BiometriaFacialEstado;
  updated_at: string;
};

function formatUnknownError(err: unknown, fallback: string) {
  if (!err) return fallback;
  if (err instanceof Error) return err.message || fallback;
  if (typeof err === 'string') return err;

  const anyErr = err as any;
  const msg = typeof anyErr?.message === 'string' ? anyErr.message : '';
  const details = typeof anyErr?.details === 'string' ? anyErr.details : '';
  const hint = typeof anyErr?.hint === 'string' ? anyErr.hint : '';
  const code = typeof anyErr?.code === 'string' ? anyErr.code : '';

  const composed = [msg, details, hint].filter(Boolean).join(' - ');

  // Friendly mapping for common Postgres errors.
  const lc = (msg + ' ' + details + ' ' + hint).toLowerCase();
  if (code === '23505' || lc.includes('duplicate key') || lc.includes('unique constraint')) {
    if (lc.includes('email')) return 'El email ya está registrado.';
    return 'Ya existe un registro con esos datos.';
  }

  return composed || code || fallback;
}

function formatISODateLocal(date: Date) {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
}

function parseISODateLocal(iso: string) {
  const parts = iso.split('-').map((v) => Number(v));
  if (parts.length !== 3 || parts.some((n) => Number.isNaN(n))) return null;
  const [y, m, d] = parts;
  return new Date(y, m - 1, d);
}

function showLinkModal(title: string, link: string, message?: string) {
  const linkElement = document.createElement('div');
  linkElement.innerHTML = `
    <div style="text-align: left;">
      <p style="margin-bottom: 12px; font-weight: 600;">${title}</p>
      ${message ? `<p style="margin-bottom: 12px; font-size: 14px;">${message}</p>` : ''}
      <div style="background: #f0fdfa; padding: 12px; border-radius: 8px; margin-bottom: 12px; word-break: break-all; font-family: monospace; font-size: 12px;">
        ${link}
      </div>
    </div>
  `;

  const tempDiv = document.createElement('div');
  tempDiv.style.cssText =
    'position: fixed; top: 50%; left: 50%; transform: translate(-50%, -50%); background: white; padding: 24px; border-radius: 12px; box-shadow: 0 25px 50px -12px rgba(0,0,0,0.25); z-index: 9999; max-width: 600px; width: 90%;';
  tempDiv.appendChild(linkElement);

  const closeBtn = document.createElement('button');
  closeBtn.textContent = 'Cerrar';
  closeBtn.style.cssText =
    'width: 100%; margin-top: 16px; padding: 12px; background: #14b8a6; color: white; border: none; border-radius: 8px; cursor: pointer; font-weight: 600;';
  closeBtn.onclick = () => {
    document.body.removeChild(overlay);
    document.body.removeChild(tempDiv);
  };
  tempDiv.appendChild(closeBtn);

  const overlay = document.createElement('div');
  overlay.style.cssText = 'position: fixed; inset: 0; background: rgba(0,0,0,0.5); z-index: 9998;';
  overlay.onclick = () => {
    document.body.removeChild(overlay);
    document.body.removeChild(tempDiv);
  };

  document.body.appendChild(overlay);
  document.body.appendChild(tempDiv);
}

export default function UsuariosPage() {
  const { sucursalActiva } = useSucursal();
  const [usuarios, setUsuarios] = useState<Usuario[]>([]);
  const [planes, setPlanes] = useState<Plan[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterEstado, setFilterEstado] = useState<string>('todos');
  const [showModal, setShowModal] = useState(false);
  const [showPlanModal, setShowPlanModal] = useState(false);
  const [editingUser, setEditingUser] = useState<Usuario | null>(null);
  const [selectedUser, setSelectedUser] = useState<Usuario | null>(null);
  const [faceCaptureDataUrl, setFaceCaptureDataUrl] = useState('');
  const [faceCaptureOpen, setFaceCaptureOpen] = useState(false);
  const [faceCaptureBusy, setFaceCaptureBusy] = useState(false);
  const [faceCaptureError, setFaceCaptureError] = useState<string | null>(null);
  const [biometriaFacialByUsuario, setBiometriaFacialByUsuario] = useState<Record<string, BiometriaFacialRow>>({});
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const streamRef = useRef<MediaStream | null>(null);

  const [formData, setFormData] = useState({
    nombre: '',
    email: '',
    telefono: '',
    whatsapp: '',
    fecha_nacimiento: '',
    tipo_membresia: '',
    fecha_inicio: '',
    fecha_vencimiento: '',
    estado: 'activo' as 'activo' | 'inactivo' | 'vencido',
    notificar_email: true,
    notificar_whatsapp: true,
  });

  useEffect(() => {
    fetchUsuarios();
    fetchPlanes();
  }, [sucursalActiva]);

  useEffect(() => {
    return () => {
      if (streamRef.current) {
        streamRef.current.getTracks().forEach((track) => track.stop());
        streamRef.current = null;
      }
    };
  }, []);

  const stopFaceCamera = () => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((track) => track.stop());
      streamRef.current = null;
    }
    setFaceCaptureOpen(false);
  };

  const startFaceCamera = async () => {
    try {
      setFaceCaptureError(null);
      if (!navigator?.mediaDevices?.getUserMedia) {
        setFaceCaptureError('Este navegador no soporta camara.');
        return;
      }
      setFaceCaptureBusy(true);
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: 'user', width: { ideal: 640 }, height: { ideal: 640 } },
        audio: false,
      });
      streamRef.current = stream;
      setFaceCaptureOpen(true);
      setTimeout(() => {
        if (videoRef.current) {
          videoRef.current.srcObject = stream;
          videoRef.current.play().catch(() => {});
        }
      }, 0);
    } catch (err) {
      setFaceCaptureError(formatUnknownError(err, 'No se pudo abrir la camara.'));
    } finally {
      setFaceCaptureBusy(false);
    }
  };

  const captureFaceSnapshot = () => {
    try {
      setFaceCaptureError(null);
      const video = videoRef.current;
      if (!video) {
        setFaceCaptureError('No se pudo acceder al video de la camara.');
        return;
      }
      const width = video.videoWidth || 640;
      const height = video.videoHeight || 640;
      const canvas = document.createElement('canvas');
      canvas.width = width;
      canvas.height = height;
      const ctx = canvas.getContext('2d');
      if (!ctx) {
        setFaceCaptureError('No se pudo capturar la imagen.');
        return;
      }
      ctx.drawImage(video, 0, 0, width, height);
      const snapshot = canvas.toDataURL('image/jpeg', 0.92);
      setFaceCaptureDataUrl(snapshot);
      stopFaceCamera();
    } catch (err) {
      setFaceCaptureError(formatUnknownError(err, 'No se pudo capturar el rostro.'));
    }
  };

  const getValidAccessToken = async () => {
    let {
      data: { session },
    } = await supabase.auth.getSession();

    const now = Math.floor(Date.now() / 1000);
    const expiresAt = session?.expires_at ?? 0;
    const isExpiredOrNearExpiry = !session?.access_token || expiresAt <= now + 60;

    if (isExpiredOrNearExpiry) {
      const { data: refreshed, error: refreshErr } = await supabase.auth.refreshSession();
      if (refreshErr || !refreshed?.session?.access_token) {
        throw new Error('Sesion expirada. Inicia sesion nuevamente.');
      }
      session = refreshed.session;
    }

    return session.access_token;
  };

  const enrollFaceForUser = async (usuarioId: string) => {
    if (!faceCaptureDataUrl) {
      throw new Error('Debes capturar el rostro del usuario.');
    }

    const invokeEnroll = async (accessToken: string) =>
      supabase.functions.invoke('usuario-facial-enroll', {
        body: {
          usuario_id: usuarioId,
          image_data_url: faceCaptureDataUrl,
        },
        headers: {
          Authorization: `Bearer ${accessToken}`,
        },
      });

    const resolveInvokeError = (invokeError: any) => {
      const status = invokeError?.context?.status;
      let details: any = null;

      if (invokeError?.context?.body) {
        try {
          details = JSON.parse(invokeError.context.body);
        } catch {
          details = null;
        }
      }

      const message = String(invokeError?.message || '').toLowerCase();
      const detailsMessage = String(details?.message || details?.error || '').toLowerCase();
      const invalidJwt =
        status === 401 || message.includes('invalid jwt') || detailsMessage.includes('invalid jwt');

      const fallbackMessage =
        details?.details
          ? `${details?.error || 'Error'}: ${details.details}`
          : details?.error || details?.message || invokeError?.message || 'No se pudo guardar la biometria facial';

      return { invalidJwt, fallbackMessage };
    };

    let accessToken = await getValidAccessToken();
    let { error: invokeError } = await invokeEnroll(accessToken);

    if (invokeError) {
      const first = resolveInvokeError(invokeError);
      if (first.invalidJwt) {
        const { data: refreshed, error: refreshErr } = await supabase.auth.refreshSession();
        if (refreshErr || !refreshed?.session?.access_token) {
          throw new Error('Sesion expirada. Inicia sesion nuevamente.');
        }

        accessToken = refreshed.session.access_token;
        const retry = await invokeEnroll(accessToken);
        invokeError = retry.error;
      }
    }

    if (invokeError) {
      const parsed = resolveInvokeError(invokeError);
      throw new Error(parsed.fallbackMessage);
    }
  };

  const fetchPlanes = async () => {
    try {
      const gimnasioId = sucursalActiva?.gimnasio_id;
      if (!gimnasioId) {
        setPlanes([]);
        return;
      }

      const { data, error: fetchError } = await supabase
        .from('planes')
        .select('*')
        .eq('gimnasio_id', gimnasioId)
        .order('precio', { ascending: true });

      if (fetchError) throw fetchError;

      const rows = (data || []) as Plan[];
      // Be permissive: if "activo" is null/undefined, treat it as active.
      setPlanes(rows.filter((p) => p.activo !== false));
    } catch (err) {
      console.error('Error fetching planes:', err);
    }
  };

  const fetchUsuarios = async () => {
    if (!sucursalActiva?.id) {
      setUsuarios([]);
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError(null);

      const query = supabase
        .from('usuarios')
        .select('*')
        .eq('sucursal_id', sucursalActiva.id)
        .order('created_at', { ascending: false });

      const { data, error: fetchError } = await query;

      if (fetchError) throw fetchError;

      const usuariosRows = (data || []) as Usuario[];
      setUsuarios(usuariosRows);

      const usuariosIds = usuariosRows.map((u) => u.id).filter(Boolean);
      if (usuariosIds.length === 0) {
        setBiometriaFacialByUsuario({});
        return;
      }

      const { data: facialRows, error: facialError } = await supabase
        .from('usuarios_biometria_facial')
        .select('usuario_id, estado, updated_at')
        .in('usuario_id', usuariosIds);

      if (facialError) {
        console.warn('No se pudo cargar estado de biometría facial:', facialError.message);
        setBiometriaFacialByUsuario({});
      } else {
        const facialMap: Record<string, BiometriaFacialRow> = {};
        for (const row of (facialRows || []) as BiometriaFacialRow[]) {
          if (!row?.usuario_id) continue;
          facialMap[row.usuario_id] = row;
        }
        setBiometriaFacialByUsuario(facialMap);
      }
    } catch (err) {
      setError(formatUnknownError(err, 'Error al cargar usuarios'));
      console.error('Error fetching usuarios:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    try {
      setError(null);
      setFaceCaptureError(null);

      // Validar que exista una sucursal seleccionada
      if (!sucursalActiva?.id) {
        setError('Debe seleccionar una sucursal antes de crear un usuario');
        return;
      }

      if (!editingUser && !faceCaptureDataUrl) {
        setError('Debes capturar el rostro para registrar el usuario.');
        return;
      }

      const userData = {
        nombre: formData.nombre,
        email: formData.email,
        telefono: formData.telefono,
        whatsapp: formData.whatsapp,
        fecha_nacimiento: formData.fecha_nacimiento,
        tipo_membresia: formData.tipo_membresia || null,
        fecha_inicio: formData.fecha_inicio || null,
        fecha_vencimiento: formData.fecha_vencimiento || null,
        estado: formData.estado,
        notificar_email: formData.notificar_email,
        notificar_whatsapp: formData.notificar_whatsapp,
        gimnasio_id: sucursalActiva.gimnasio_id || null,
        sucursal_id: sucursalActiva.id,
        updated_at: new Date().toISOString(),
      };

      if (editingUser) {
        const { error: updateError } = await supabase
          .from('usuarios')
          .update(userData)
          .eq('id', editingUser.id);

        if (updateError) throw updateError;
        if (faceCaptureDataUrl) {
          await enrollFaceForUser(editingUser.id);
        }
      } else {
        // Crear nuevo usuario
        const { data: newUser, error: insertError } = await supabase
          .from('usuarios')
          .insert([userData])
          .select()
          .single();

        if (insertError) throw insertError;

        if (newUser) {
          try {
            await enrollFaceForUser(newUser.id);
          } catch (faceErr) {
            await supabase.from('usuarios').delete().eq('id', newUser.id);
            throw faceErr;
          }
        }

        // Enviar link de activación automáticamente
        if (newUser) {
          try {
            const { data: { session } } = await supabase.auth.getSession();
            
            const response = await fetch(
              `${import.meta.env.VITE_PUBLIC_SUPABASE_URL}/functions/v1/enviar-activacion`,
              {
                method: 'POST',
                headers: {
                  'Content-Type': 'application/json',
                  'Authorization': `Bearer ${session?.access_token}`,
                },
                body: JSON.stringify({
                  usuario_id: newUser.id,
                }),
              }
            );

            const result = await response.json();

            if (!response.ok) {
              console.error('Error al enviar link de activación:', result);
              setError(`Usuario creado, pero no se pudo enviar el link de activación: ${result.error || 'Error desconocido'}`);
            }
          } catch (err) {
            console.error('Error al enviar link de activación:', err);
            setError(formatUnknownError(err, 'Usuario creado, pero no se pudo enviar el link de activación'));
          }
        }
      }

      setShowModal(false);
      resetForm();
      fetchUsuarios();
    } catch (err) {
      setError(formatUnknownError(err, 'Error al guardar usuario'));
      console.error('Error saving usuario:', err);
    }
  };

  const handleDelete = async (id: string) => {
    if (!confirm('¿Estás seguro de eliminar este usuario?')) return;

    try {
      setError(null);
      const { error: deleteError } = await supabase
        .from('usuarios')
        .delete()
        .eq('id', id);

      if (deleteError) throw deleteError;

      fetchUsuarios();
    } catch (err) {
      setError(formatUnknownError(err, 'Error al eliminar usuario'));
      console.error('Error deleting usuario:', err);
    }
  };

  const handleEdit = (usuario: Usuario) => {
    stopFaceCamera();
    setFaceCaptureDataUrl('');
    setFaceCaptureError(null);
    setEditingUser(usuario);
    setFormData({
      nombre: usuario.nombre,
      email: usuario.email,
      telefono: usuario.telefono,
      whatsapp: usuario.whatsapp,
      fecha_nacimiento: usuario.fecha_nacimiento,
      tipo_membresia: usuario.tipo_membresia,
      fecha_inicio: usuario.fecha_inicio,
      fecha_vencimiento: usuario.fecha_vencimiento,
      estado: usuario.estado,
      notificar_email: usuario.notificar_email,
      notificar_whatsapp: usuario.notificar_whatsapp,
    });
    setShowModal(true);
  };

  const handleResetPassword = async (usuario: Usuario) => {
    if (!usuario.auth_id) {
      setError('El usuario no tiene cuenta activada. Envia el link de activacion primero.');
      return;
    }
    if (!confirm('Enviar link para restablecer contrasena a este usuario?')) return;

    try {
      setError(null);
      const getAccessToken = async () => {
        let {
          data: { session },
        } = await supabase.auth.getSession();

        if (!session?.access_token) {
          const { data: refreshed, error: refreshErr } =
            await supabase.auth.refreshSession();
          if (refreshErr || !refreshed?.session?.access_token) {
            throw new Error('Tu sesion expiro. Vuelve a iniciar sesion.');
          }
          session = refreshed.session;
        }

        return session.access_token;
      };

      const invokeReset = async (accessToken: string) =>
        supabase.functions.invoke('usuarios-reset-password', {
          body: { usuario_id: usuario.id },
          headers: {
            Authorization: `Bearer ${accessToken}`,
          },
        });

      let accessToken = await getAccessToken();
      let { data, error: invokeError } = await invokeReset(accessToken);

      if (invokeError) {
        const response = (invokeError as any)?.context;
        let details: any = null;
        if (response?.body) {
          try {
            details = JSON.parse(response.body);
          } catch {
            details = null;
          }
        }
        const status = (invokeError as any)?.context?.status;
        const isInvalidJwt =
          status === 401 ||
          invokeError.message?.toLowerCase?.().includes('invalid jwt') ||
          details?.message?.toLowerCase?.().includes('invalid jwt');

        if (isInvalidJwt) {
          const { data: refreshed, error: refreshErr } =
            await supabase.auth.refreshSession();
          if (refreshErr || !refreshed?.session?.access_token) {
            throw new Error('Tu sesion expiro. Vuelve a iniciar sesion.');
          }
          accessToken = refreshed.session.access_token;
          const retry = await invokeReset(accessToken);
          data = retry.data;
          invokeError = retry.error;

          if (!invokeError) {
            if (data?.emailSent) {
              alert(`Link enviado a ${data.email}`);
            } else if (data?.link) {
              showLinkModal(
                'Link para restablecer contrasena',
                data.link,
                data.warning || 'Copia este link y envialo manualmente al usuario.',
              );
            }
            return;
          }

          const retryResponse = (invokeError as any)?.context;
          let retryDetails: any = null;
          if (retryResponse?.body) {
            try {
              retryDetails = JSON.parse(retryResponse.body);
            } catch {
              retryDetails = null;
            }
          }
          details = retryDetails ?? details;
        }
        if (details?.link) {
          showLinkModal(
            'Link para restablecer contrasena',
            details.link,
            details.details || details.error,
          );
          return;
        }
        throw new Error(details?.error || invokeError.message || 'No se pudo enviar el link');
      }

      if (data?.emailSent) {
        alert(`Link enviado a ${data.email}`);
      } else if (data?.link) {
        showLinkModal(
          'Link para restablecer contrasena',
          data.link,
          data.warning || 'Copia este link y envialo manualmente al usuario.',
        );
      }
    } catch (err) {
      setError(formatUnknownError(err, 'Error al enviar link de restablecimiento'));
      console.error('Error reset password:', err);
    }
  };
  const handleAsignarPlan = async (plan: any) => {
    if (!selectedUser) return;

    try {
      setError(null);
      const fechaInicio = new Date();
      const fechaVencimiento = new Date();
      fechaVencimiento.setDate(fechaVencimiento.getDate() + plan.duracion_dias);

      const { error: updateError } = await supabase
        .from('usuarios')
        .update({
          tipo_membresia: plan.nombre,
          fecha_inicio: fechaInicio.toISOString().split('T')[0],
          fecha_vencimiento: fechaVencimiento.toISOString().split('T')[0],
          estado: 'activo',
          updated_at: new Date().toISOString(),
        })
        .eq('id', selectedUser.id);

      if (updateError) throw updateError;

      setShowPlanModal(false);
      setSelectedUser(null);
      fetchUsuarios();
    } catch (err: any) {
      setError(err.message);
    }
  };

  const resetForm = () => {
    stopFaceCamera();
    setFaceCaptureDataUrl('');
    setFaceCaptureError(null);
    setFormData({
      nombre: '',
      email: '',
      telefono: '',
      whatsapp: '',
      fecha_nacimiento: '',
      tipo_membresia: '',
      fecha_inicio: '',
      fecha_vencimiento: '',
      estado: 'activo',
      notificar_email: true,
      notificar_whatsapp: true,
    });
    setEditingUser(null);
  };

  const filteredUsuarios = usuarios.filter((usuario) => {
    const matchesSearch = usuario.nombre.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         usuario.email.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesEstado = filterEstado === 'todos' || usuario.estado === filterEstado;
    return matchesSearch && matchesEstado;
  });

  const estadisticas = {
    total: usuarios.length,
    activos: usuarios.filter(u => u.estado === 'activo').length,
    vencidos: usuarios.filter(u => u.estado === 'vencido').length,
    inactivos: usuarios.filter(u => u.estado === 'inactivo').length,
  };

  const getColorClasses = (color: string) => {
    const colorMap: Record<string, { bg: string; text: string; icon: string }> = {
      teal: { bg: 'bg-teal-100', text: 'text-teal-600', icon: 'text-teal-600' },
      emerald: { bg: 'bg-emerald-100', text: 'text-emerald-600', icon: 'text-emerald-600' },
      amber: { bg: 'bg-amber-100', text: 'text-amber-600', icon: 'text-amber-600' },
      rose: { bg: 'bg-rose-100', text: 'text-rose-600', icon: 'text-rose-600' },
      orange: { bg: 'bg-orange-100', text: 'text-orange-600', icon: 'text-orange-600' },
      cyan: { bg: 'bg-cyan-100', text: 'text-cyan-600', icon: 'text-cyan-600' },
      lime: { bg: 'bg-lime-100', text: 'text-lime-600', icon: 'text-lime-600' },
      fuchsia: { bg: 'bg-fuchsia-100', text: 'text-fuchsia-600', icon: 'text-fuchsia-600' },
      sky: { bg: 'bg-sky-100', text: 'text-sky-600', icon: 'text-sky-600' },
      red: { bg: 'bg-red-100', text: 'text-red-600', icon: 'text-red-600' },
      yellow: { bg: 'bg-yellow-100', text: 'text-yellow-600', icon: 'text-yellow-600' },
      slate: { bg: 'bg-slate-100', text: 'text-slate-600', icon: 'text-slate-600' },
    };
    return colorMap[color] || colorMap.teal;
  };

  const getBiometriaBadge = (usuarioId: string) => {
    const row = biometriaFacialByUsuario[usuarioId];
    if (!row) {
      return { label: 'Sin captura', className: 'bg-gray-100 text-gray-700' };
    }
    if (row.estado === 'enrolado') {
      return { label: 'Enrolado', className: 'bg-green-100 text-green-700' };
    }
    if (row.estado === 'pendiente_validacion') {
      return { label: 'Pendiente', className: 'bg-amber-100 text-amber-700' };
    }
    return { label: 'Rechazado', className: 'bg-red-100 text-red-700' };
  };
  const editingBiometria = editingUser ? biometriaFacialByUsuario[editingUser.id] : null;

  if (loading) {
    return (
      <AdminLayout>
        <div className="flex items-center justify-center h-96">
          <div className="text-center">
            <i className="ri-loader-4-line text-4xl text-teal-500 animate-spin"></i>
            <p className="mt-4 text-gray-600">Cargando usuarios...</p>
          </div>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="p-8">
        <div className="flex justify-between items-center mb-8">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">Gestión de Usuarios</h1>
            <p className="text-gray-600 mt-1">Administra los miembros del gimnasio</p>
          </div>
          <button
            onClick={() => {
              resetForm();
              setShowModal(true);
            }}
            className="bg-teal-500 text-white px-6 py-3 rounded-lg hover:bg-teal-600 transition-colors flex items-center gap-2 whitespace-nowrap"
          >
            <i className="ri-user-add-line"></i>
            Nuevo Usuario
          </button>
        </div>

        {error && (
          <div className="mb-6 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg flex items-center gap-2">
            <i className="ri-error-warning-line"></i>
            {error}
          </div>
        )}

        <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
          <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-100">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-gray-600 text-sm">Total Usuarios</p>
                <p className="text-3xl font-bold text-gray-900 mt-1">{estadisticas.total}</p>
              </div>
              <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center">
                <i className="ri-group-line text-2xl text-blue-600"></i>
              </div>
            </div>
          </div>

          <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-100">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-gray-600 text-sm">Activos</p>
                <p className="text-3xl font-bold text-green-600 mt-1">{estadisticas.activos}</p>
              </div>
              <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center">
                <i className="ri-checkbox-circle-line text-2xl text-green-600"></i>
              </div>
            </div>
          </div>

          <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-100">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-gray-600 text-sm">Vencidos</p>
                <p className="text-3xl font-bold text-red-600 mt-1">{estadisticas.vencidos}</p>
              </div>
              <div className="w-12 h-12 bg-red-100 rounded-lg flex items-center justify-center">
                <i className="ri-time-line text-2xl text-red-600"></i>
              </div>
            </div>
          </div>

          <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-100">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-gray-600 text-sm">Inactivos</p>
                <p className="text-3xl font-bold text-gray-600 mt-1">{estadisticas.inactivos}</p>
              </div>
              <div className="w-12 h-12 bg-gray-100 rounded-lg flex items-center justify-center">
                <i className="ri-user-unfollow-line text-2xl text-gray-600"></i>
              </div>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6 mb-6">
          <div className="flex flex-col md:flex-row gap-4">
            <div className="flex-1 relative">
              <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-gray-400"></i>
              <input
                type="text"
                placeholder="Buscar por nombre o email..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm"
              />
            </div>
            <select
              value={filterEstado}
              onChange={(e) => setFilterEstado(e.target.value)}
              className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm"
            >
              <option value="todos">Todos los estados</option>
              <option value="activo">Activos</option>
              <option value="vencido">Vencidos</option>
              <option value="inactivo">Inactivos</option>
            </select>
          </div>
        </div>

        <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50 border-b border-gray-200">
                <tr>
                  <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Usuario</th>
                  <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Contacto</th>
                  <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Membresía</th>
                  <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Vencimiento</th>
                  <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Estado</th>
                  <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Facial</th>
                  <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Acciones</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {filteredUsuarios.map((usuario) => {
                  const biometria = getBiometriaBadge(usuario.id);
                  const biometriaRow = biometriaFacialByUsuario[usuario.id];
                  return (
                  <tr key={usuario.id} className="hover:bg-gray-50 transition-colors">
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 bg-teal-100 rounded-full flex items-center justify-center">
                          <span className="text-teal-600 font-semibold text-sm">
                            {usuario.nombre.charAt(0).toUpperCase()}
                          </span>
                        </div>
                        <div>
                          <p className="font-medium text-gray-900">{usuario.nombre}</p>
                          <p className="text-sm text-gray-500">{usuario.email}</p>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="text-sm">
                        <p className="text-gray-900">{usuario.telefono}</p>
                        <p className="text-gray-500">{usuario.whatsapp}</p>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-purple-100 text-purple-700">
                        {usuario.tipo_membresia || 'Sin plan'}
                      </span>
                    </td>
                    <td className="px-6 py-4">
                      <p className="text-sm text-gray-900">
                        {usuario.fecha_vencimiento ? new Date(usuario.fecha_vencimiento).toLocaleDateString('es-CL') : '-'}
                      </p>
                    </td>
                    <td className="px-6 py-4">
                      <span className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium ${
                        usuario.estado === 'activo' ? 'bg-green-100 text-green-700' :
                        usuario.estado === 'vencido' ? 'bg-red-100 text-red-700' :
                        'bg-gray-100 text-gray-700'
                      }`}>
                        {usuario.estado.charAt(0).toUpperCase() + usuario.estado.slice(1)}
                      </span>
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex flex-col gap-1">
                        <span className={`inline-flex items-center w-fit px-3 py-1 rounded-full text-xs font-semibold ${biometria.className}`}>
                          {biometria.label}
                        </span>
                        {biometriaRow?.updated_at && (
                          <span className="text-xs text-gray-500">
                            {new Date(biometriaRow.updated_at).toLocaleString('es-CL')}
                          </span>
                        )}
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-2">
                        <button
                          onClick={() => handleEdit(usuario)}
                          className="w-8 h-8 flex items-center justify-center text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                          title="Editar"
                        >
                          <i className="ri-edit-line"></i>
                        </button>
                        <button
                          onClick={() => {
                            handleEdit(usuario);
                            setTimeout(() => {
                              startFaceCamera().catch(() => {});
                            }, 120);
                          }}
                          className="w-8 h-8 flex items-center justify-center text-indigo-600 hover:bg-indigo-50 rounded-lg transition-colors"
                          title="Recapturar rostro"
                        >
                          <i className="ri-camera-line"></i>
                        </button>
                        {!usuario.auth_id && (
                          <button
                            onClick={async () => {
                              try {
                                setError(null);
                                const {
                                  data: { session },
                                } = await supabase.auth.getSession();

                                if (!session?.access_token) {
                                  throw new Error('Tu sesión expiró. Vuelve a iniciar sesión.');
                                }
                                
                                const response = await fetch(
                                  `${import.meta.env.VITE_PUBLIC_SUPABASE_URL}/functions/v1/enviar-activacion`,
                                  {
                                    method: 'POST',
                                    headers: {
                                      'Content-Type': 'application/json',
                                      'Authorization': `Bearer ${session.access_token}`,
                                    },
                                    body: JSON.stringify({
                                      usuario_id: usuario.id,
                                    }),
                                  }
                                );

                                const result = await response.json();

                                if (!response.ok) {
                                  // Si el error es de Resend por dominio no verificado
                                  if (result.resendError?.statusCode === 403) {
                                    // Mostrar el link de forma amigable
                                    const linkElement = document.createElement('div');
                                    linkElement.innerHTML = `
                                      <div style="text-align: left;">
                                        <p style="margin-bottom: 12px; font-weight: 600;">⚠️ Email no enviado - Dominio no verificado</p>
                                        <p style="margin-bottom: 12px; font-size: 14px;">Copia este link y envíaselo al usuario manualmente:</p>
                                        <div style="background: #f0fdfa; padding: 12px; border-radius: 8px; margin-bottom: 12px; word-break: break-all; font-family: monospace; font-size: 12px;">
                                          ${result.link}
                                        </div>
                                        <p style="font-size: 13px; color: #64748b;">
                                          💡 Para enviar emails automáticamente, verifica tu dominio en 
                                          <a href="https://resend.com/domains" target="_blank" style="color: #14b8a6;">resend.com/domains</a>
                                        </p>
                                      </div>
                                    `;
                                    
                                    const tempDiv = document.createElement('div');
                                    tempDiv.style.cssText = 'position: fixed; top: 50%; left: 50%; transform: translate(-50%, -50%); background: white; padding: 24px; border-radius: 12px; box-shadow: 0 25px 50px -12px rgba(0,0,0,0.25); z-index: 9999; max-width: 600px; width: 90%;';
                                    tempDiv.appendChild(linkElement);
                                    
                                    const closeBtn = document.createElement('button');
                                    closeBtn.textContent = 'Cerrar';
                                    closeBtn.style.cssText = 'width: 100%; margin-top: 16px; padding: 12px; background: #14b8a6; color: white; border: none; border-radius: 8px; cursor: pointer; font-weight: 600;';
                                    closeBtn.onclick = () => {
                                      document.body.removeChild(overlay);
                                      document.body.removeChild(tempDiv);
                                    };
                                    tempDiv.appendChild(closeBtn);
                                    
                                    const overlay = document.createElement('div');
                                    overlay.style.cssText = 'position: fixed; inset: 0; background: rgba(0,0,0,0.5); z-index: 9998;';
                                    overlay.onclick = () => {
                                      document.body.removeChild(overlay);
                                      document.body.removeChild(tempDiv);
                                    };
                                    
                                    document.body.appendChild(overlay);
                                    document.body.appendChild(tempDiv);
                                    
                                    return;
                                  }
                                  
                                  throw new Error(result.error || `Error HTTP: ${response.status}`);
                                }

                                if (result.success) {
                                  alert(`✅ Link de activación enviado exitosamente a ${result.email}`);
                                  fetchUsuarios();
                                }
                              } catch (err) {
                                setError(`Error: ${formatUnknownError(err, 'Error al enviar link de activación')}`);
                                console.error('Error completo:', err);
                              }
                            }}
                            className="w-8 h-8 flex items-center justify-center text-green-600 hover:bg-green-50 rounded-lg transition-colors"
                            title="Enviar Link de Activación"
                          >
                            <i className="ri-mail-send-line"></i>
                          </button>
                        )}
                        {usuario.auth_id && (
                          <button
                            onClick={() => handleResetPassword(usuario)}
                            className="w-8 h-8 flex items-center justify-center text-amber-600 hover:bg-amber-50 rounded-lg transition-colors"
                            title="Restablecer contrasena"
                          >
                            <i className="ri-key-line"></i>
                          </button>
                        )}
                        <button
                          onClick={() => {
                            setSelectedUser(usuario);
                            setShowPlanModal(true);
                          }}
                          className="w-8 h-8 flex items-center justify-center text-teal-600 hover:bg-teal-50 rounded-lg transition-colors"
                          title="Asignar Plan"
                        >
                          <i className="ri-vip-crown-line"></i>
                        </button>
                        <button
                          onClick={() => handleDelete(usuario.id)}
                          className="w-8 h-8 flex items-center justify-center text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                          title="Eliminar"
                        >
                          <i className="ri-delete-bin-line"></i>
                        </button>
                      </div>
                    </td>
                  </tr>
                )})}
              </tbody>
            </table>
          </div>

          {filteredUsuarios.length === 0 && (
            <div className="text-center py-12">
              <i className="ri-user-line text-5xl text-gray-300"></i>
              <p className="text-gray-500 mt-4">No se encontraron usuarios</p>
            </div>
          )}
        </div>

        {showModal && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-xl shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
              <div className="p-6 border-b border-gray-200">
                <h2 className="text-2xl font-bold text-gray-900">
                  {editingUser ? 'Editar Usuario' : 'Nuevo Usuario'}
                </h2>
              </div>

              <form onSubmit={handleSubmit} className="p-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Nombre Completo *
                    </label>
                    <input
                      type="text"
                      required
                      value={formData.nombre}
                      onChange={(e) => setFormData({ ...formData, nombre: e.target.value })}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Email *
                    </label>
                    <input
                      type="email"
                      required
                      value={formData.email}
                      onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Teléfono *
                    </label>
                    <input
                      type="tel"
                      required
                      value={formData.telefono}
                      onChange={(e) => setFormData({ ...formData, telefono: e.target.value })}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      WhatsApp *
                    </label>
                    <input
                      type="tel"
                      required
                      value={formData.whatsapp}
                      onChange={(e) => setFormData({ ...formData, whatsapp: e.target.value })}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Fecha de Nacimiento *
                    </label>
                    <input
                      type="date"
                      required
                      value={formData.fecha_nacimiento}
                      onChange={(e) => setFormData({ ...formData, fecha_nacimiento: e.target.value })}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Estado *
                    </label>
                    <select
                      required
                      value={formData.estado}
                      onChange={(e) => setFormData({ ...formData, estado: e.target.value as 'activo' | 'inactivo' | 'vencido' })}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm"
                    >
                      <option value="activo">Activo</option>
                      <option value="inactivo">Inactivo</option>
                      <option value="vencido">Vencido</option>
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Plan de Membresía (opcional)
                    </label>
                    {planes.length > 0 ? (
                      <select
                        value={formData.tipo_membresia}
                        onChange={(e) => {
                          const next = e.target.value;
                          const plan = planes.find((p) => p.nombre === next);
                          const nextInicio = formData.fecha_inicio || formatISODateLocal(new Date());
                          const nextVenc =
                            plan && plan.duracion_dias > 0
                              ? (() => {
                                  const base = parseISODateLocal(nextInicio) || new Date();
                                  base.setDate(base.getDate() + plan.duracion_dias);
                                  return formatISODateLocal(base);
                                })()
                              : formData.fecha_vencimiento;

                          setFormData({
                            ...formData,
                            tipo_membresia: next,
                            // Auto-fill dates when a plan is selected (user can still edit them afterwards).
                            fecha_inicio: plan ? nextInicio : formData.fecha_inicio,
                            fecha_vencimiento: plan ? nextVenc : formData.fecha_vencimiento,
                          });
                        }}
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm"
                      >
                        <option value="">Sin plan</option>
                        {planes.map((p) => (
                          <option key={p.id} value={p.nombre}>
                            {p.nombre} ({p.duracion_dias} días)
                          </option>
                        ))}
                      </select>
                    ) : (
                      <div className="text-sm text-gray-500 border border-gray-200 rounded-lg px-4 py-2">
                        No hay planes cargados (o no se pudieron cargar). Puedes crear planes en “Planes de Membresía”.
                      </div>
                    )}
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Fecha de Inicio
                    </label>
                    <input
                      type="date"
                      value={formData.fecha_inicio}
                      onChange={(e) => setFormData({ ...formData, fecha_inicio: e.target.value })}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Fecha de Vencimiento
                    </label>
                    <input
                      type="date"
                      value={formData.fecha_vencimiento}
                      onChange={(e) => setFormData({ ...formData, fecha_vencimiento: e.target.value })}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm"
                    />
                  </div>
                </div>

                <div className="mt-6 rounded-xl border border-gray-200 p-4 bg-gray-50">
                  <div className="flex items-center justify-between gap-4">
                    <div>
                      <p className="text-sm font-semibold text-gray-900">Captura facial</p>
                      <p className="text-xs text-gray-600 mt-1">
                        {editingUser
                          ? 'Opcional al editar: puedes actualizar la biometría facial.'
                          : 'Obligatorio al crear usuario para reconocimiento facial.'}
                      </p>
                    </div>
                    <div className="flex items-center gap-2">
                      <button
                        type="button"
                        onClick={startFaceCamera}
                        disabled={faceCaptureBusy}
                        className="px-3 py-2 rounded-lg border border-teal-200 text-teal-700 bg-white hover:bg-teal-50 text-sm disabled:opacity-60"
                      >
                        <i className="ri-camera-line mr-1"></i>
                        Abrir cámara
                      </button>
                      {faceCaptureDataUrl && (
                        <button
                          type="button"
                          onClick={() => setFaceCaptureDataUrl('')}
                          className="px-3 py-2 rounded-lg border border-gray-200 text-gray-700 bg-white hover:bg-gray-100 text-sm"
                        >
                          Limpiar
                        </button>
                      )}
                    </div>
                  </div>

                  {editingUser && (
                    <div className="mt-3 rounded-lg border border-gray-200 bg-white p-3 text-xs text-gray-700">
                      <p className="font-medium text-gray-900">
                        Estado actual: {getBiometriaBadge(editingUser.id).label}
                      </p>
                      {editingBiometria?.updated_at && (
                        <p className="mt-1 text-gray-500">
                          Última actualización: {new Date(editingBiometria.updated_at).toLocaleString('es-CL')}
                        </p>
                      )}
                    </div>
                  )}

                  {faceCaptureError && (
                    <div className="mt-3 text-sm text-red-600 flex items-center gap-2">
                      <i className="ri-error-warning-line"></i>
                      {faceCaptureError}
                    </div>
                  )}

                  {faceCaptureOpen && (
                    <div className="mt-4">
                      <div className="rounded-lg overflow-hidden bg-black">
                        <video ref={videoRef} className="w-full max-h-64 object-cover" playsInline muted />
                      </div>
                      <div className="mt-3 flex gap-2">
                        <button
                          type="button"
                          onClick={captureFaceSnapshot}
                          className="px-4 py-2 bg-teal-500 text-white rounded-lg hover:bg-teal-600 text-sm"
                        >
                          Capturar rostro
                        </button>
                        <button
                          type="button"
                          onClick={stopFaceCamera}
                          className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-100 text-sm"
                        >
                          Cancelar
                        </button>
                      </div>
                    </div>
                  )}

                  {faceCaptureDataUrl && (
                    <div className="mt-4">
                      <p className="text-xs text-gray-600 mb-2">Vista previa capturada</p>
                      <img src={faceCaptureDataUrl} alt="Captura facial" className="w-36 h-36 object-cover rounded-lg border border-gray-200" />
                    </div>
                  )}
                </div>

                <div className="mt-6 space-y-3">
                  <label className="flex items-center gap-3 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={formData.notificar_email}
                      onChange={(e) => setFormData({ ...formData, notificar_email: e.target.checked })}
                      className="w-5 h-5 text-teal-500 rounded focus:ring-2 focus:ring-teal-500"
                    />
                    <span className="text-sm text-gray-700">Notificar por Email</span>
                  </label>

                  <label className="flex items-center gap-3 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={formData.notificar_whatsapp}
                      onChange={(e) => setFormData({ ...formData, notificar_whatsapp: e.target.checked })}
                      className="w-5 h-5 text-teal-500 rounded focus:ring-2 focus:ring-teal-500"
                    />
                    <span className="text-sm text-gray-700">Notificar por WhatsApp</span>
                  </label>
                </div>

                <div className="flex gap-3 mt-6">
                  <button
                    type="button"
                    onClick={() => {
                      setShowModal(false);
                      resetForm();
                    }}
                    className="flex-1 px-6 py-3 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors whitespace-nowrap"
                  >
                    Cancelar
                  </button>
                  <button
                    type="submit"
                    className="flex-1 px-6 py-3 bg-teal-500 text-white rounded-lg hover:bg-teal-600 transition-colors whitespace-nowrap"
                  >
                    {editingUser ? 'Actualizar' : 'Crear Usuario'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

        {showPlanModal && selectedUser && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-xl shadow-xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
              <div className="p-6 border-b border-gray-200">
                <h2 className="text-2xl font-bold text-gray-900">
                  Asignar Plan a {selectedUser.nombre}
                </h2>
              </div>

              <div className="p-6">
                {planes.length === 0 ? (
                  <div className="text-center py-12">
                    <i className="ri-vip-crown-line text-5xl text-gray-300"></i>
                    <p className="text-gray-500 mt-4">No hay planes activos disponibles</p>
                    <p className="text-sm text-gray-400 mt-2">Crea planes en la sección de Planes</p>
                  </div>
                ) : (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    {planes.map((plan) => {
                      const colors = getColorClasses(plan.color || 'teal');
                      return (
                        <div
                          key={plan.id}
                          className="border-2 border-gray-200 rounded-xl p-6 hover:border-teal-500 transition-colors cursor-pointer"
                          onClick={() => handleAsignarPlan(plan)}
                        >
                          <div className="flex items-start justify-between mb-4">
                            <div>
                              <h3 className="text-xl font-bold text-gray-900">{plan.nombre}</h3>
                              <p className="text-sm text-gray-600 mt-1">{plan.descripcion}</p>
                            </div>
                            <div className={`w-12 h-12 ${colors.bg} rounded-lg flex items-center justify-center`}>
                              <i className={`ri-vip-crown-line text-2xl ${colors.icon}`}></i>
                            </div>
                          </div>
                          <div className="flex items-baseline gap-1 mb-4">
                            <span className={`text-3xl font-bold ${colors.text}`}>
                              ${plan.precio.toLocaleString('es-CL')}
                            </span>
                            <span className="text-gray-600">CLP</span>
                          </div>
                          <button className="w-full bg-teal-500 text-white py-2 rounded-lg hover:bg-teal-600 transition-colors whitespace-nowrap">
                            Seleccionar Plan
                          </button>
                        </div>
                      );
                    })}
                  </div>
                )}

                <div className="mt-6">
                  <button
                    onClick={() => {
                      setShowPlanModal(false);
                      setSelectedUser(null);
                    }}
                    className="w-full px-6 py-3 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors whitespace-nowrap"
                  >
                    Cancelar
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </AdminLayout>
  );
}
